import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, CheckCircle2, Trash2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = { type: "payment", contact_id: "", amount: "", currency: "TRY", due_date: todayISO(), description: "", bank_account_id: "" };

export default function Upcoming() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [u, c, b] = await Promise.all([api.get("/upcoming"), api.get("/contacts"), api.get("/bank-accounts")]);
    setItems(u.data); setContacts(c.data); setBanks(b.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => filter === "all" ? true : i.status === filter);

  const save = async () => {
    const payload = { ...form, amount: parseFloat(form.amount), contact_id: form.contact_id || null, bank_account_id: form.bank_account_id || null };
    if (!payload.amount || !payload.due_date) { toast.error("Tutar ve tarih zorunludur"); return; }
    try {
      await api.post("/upcoming", payload);
      toast.success("Eklendi"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/upcoming/${id}/mark-paid`);
      toast.success("Tahsil/Ödeme yapıldı olarak işaretlendi");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istiyor musunuz?")) return;
    await api.delete(`/upcoming/${id}`);
    toast.success("Silindi"); await load();
  };

  const contactName = (id) => contacts.find((c) => c.id === id)?.name || "—";
  const isOverdue = (d) => d < todayISO();

  const pendingSummary = items.filter((i) => i.status === "pending").reduce((acc, i) => {
    if (i.type === "payment") acc.payments += i.amount_try;
    else acc.collections += i.amount_try;
    return acc;
  }, { payments: 0, collections: 0 });

  return (
    <div className="space-y-6 slide-up" data-testid="upcoming-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Nakit Akışı</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Yaklaşan Ödemeler & Tahsilatlar</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Vadesi gelen ödeme ve tahsilatları takip edin, hatırlatıcı alın.</p>
        </div>
        <Button data-testid="new-upcoming-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-indigo-500 hover:bg-indigo-600"><Plus className="w-4 h-4 mr-2"/>Yeni Kayıt</Button>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="card-elev p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><Clock className="w-5 h-5"/></div>
            <div>
              <div className="text-xs text-[#94A3B8] uppercase">Bekleyen Tahsilatlar (TRY)</div>
              <div className="text-2xl font-bold mono mt-1">{formatMoney(pendingSummary.collections, "TRY")}</div>
            </div>
          </div>
        </div>
        <div className="card-elev p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center"><Clock className="w-5 h-5"/></div>
            <div>
              <div className="text-xs text-[#94A3B8] uppercase">Bekleyen Ödemeler (TRY)</div>
              <div className="text-2xl font-bold mono mt-1">{formatMoney(pendingSummary.payments, "TRY")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-[#131D31] border border-[#1E293B]">
            <TabsTrigger value="pending">Bekleyen</TabsTrigger>
            <TabsTrigger value="paid">Tamamlanan</TabsTrigger>
            <TabsTrigger value="all">Tümü</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="card-elev overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F172A] text-[#94A3B8]">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Vade</th>
              <th className="text-left px-5 py-3 font-medium">Tür</th>
              <th className="text-left px-5 py-3 font-medium">Cari</th>
              <th className="text-left px-5 py-3 font-medium">Açıklama</th>
              <th className="text-right px-5 py-3 font-medium">Tutar</th>
              <th className="text-right px-5 py-3 font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-[#94A3B8]">Kayıt yok.</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-[#1E293B] hover:bg-[#0F172A]/50" data-testid={`upcoming-row-${u.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {u.status === "pending" && isOverdue(u.due_date) && <AlertTriangle className="w-3.5 h-3.5 text-amber-400"/>}
                    <span className={isOverdue(u.due_date) && u.status === "pending" ? "text-amber-400" : "text-[#94A3B8]"}>{formatDate(u.due_date)}</span>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${u.type === "collection" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
                    {u.type === "collection" ? "Tahsilat" : "Ödeme"}
                  </span>
                </td>
                <td className="px-5 py-3 text-[#94A3B8]">{contactName(u.contact_id)}</td>
                <td className="px-5 py-3 text-[#94A3B8]">{u.description || "—"}</td>
                <td className={`px-5 py-3 text-right mono font-semibold ${u.type === "collection" ? "text-emerald-400" : "text-red-400"}`}>
                  {formatMoney(u.amount, u.currency)}
                </td>
                <td className="px-5 py-3 text-right">
                  {u.status === "pending" ? (
                    <>
                      <button data-testid={`markpaid-${u.id}`} onClick={() => markPaid(u.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-emerald-400 mr-1" title="Tamamlandı"><CheckCircle2 className="w-4 h-4"/></button>
                      <button data-testid={`delete-upcoming-${u.id}`} onClick={() => remove(u.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                    </>
                  ) : (
                    <span className="text-xs text-emerald-400 flex items-center justify-end gap-1"><CheckCircle2 className="w-3.5 h-3.5"/>Tamamlandı</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131D31] border-[#1E293B] text-white">
          <DialogHeader><DialogTitle>Yeni Ödeme/Tahsilat</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#94A3B8]">Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="upcoming-type" className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="payment">Ödeme (giden)</SelectItem>
                    <SelectItem value="collection">Tahsilat (gelen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Vade Tarihi</Label>
                <Input type="date" data-testid="upcoming-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Tutar</Label>
                <Input type="number" step="0.01" data-testid="upcoming-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-[#94A3B8]">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8]">Cari (opsiyonel)</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="none">—</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8]">Banka Hesabı (opsiyonel)</Label>
                <Select value={form.bank_account_id || "none"} onValueChange={(v) => setForm({ ...form, bank_account_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent className="bg-[#131D31] border-[#1E293B]">
                    <SelectItem value="none">—</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-[#94A3B8]">Açıklama</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="upcoming-save-btn" className="bg-indigo-500 hover:bg-indigo-600">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
